import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import Box from 'common/components/box/Box';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import Select from 'common/components/select/Select';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import { FuelConsumptionKind, FuelConsumptionPerAssetDashboardDto } from 'api/dashboard/dtos/FuelConsumptionPerAssetDashboardDto';
import { VehicleFuelKindsDto } from 'api/dashboard/dtos/VehicleFuelKindsDto';
import DashboardService from 'api/dashboard/DashboardService';
import Logger from 'common/services/Logger';
import { LOGGER_LOG_TYPE } from 'Config';
import useWidgetFilters from '../hooks/useWidgetFilters';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import { FiltersFormModel } from '../filters/FiltersView';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import FuelConsumptionPerAssetChart from './FuelConsumptionPerAssetChart';
import { getConsumptionTranslationKeys } from './fuelConsumptionUtils';
import styles from './FuelConsumptionPerAsset.module.scss';

const MIN_VEHICLES = 1;
const MAX_VEHICLES = 5;

type Props = {
	vehicles?: SelectValueLabel[];
	fuelKind: FuelConsumptionKind;
	// Modo "veículo fixo" (detalhe do ativo): bloqueia ao ativo atual, esconde o seletor
	// e segue o intervalo de datas partilhado (filters). Sem isto = modo comparação (1-5 ativos).
	lockedVehicleId?: string;
	filters?: FiltersFormModel;
};

const FuelConsumptionPerAsset = ({ vehicles = [], fuelKind, lockedVehicleId, filters }: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [selectedVehicles, setSelectedVehicles] = useState<SelectValueLabel[]>([]);
	const [data, setData] = useState<FuelConsumptionPerAssetDashboardDto | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [fuelKinds, setFuelKinds] = useState<VehicleFuelKindsDto[] | null>(null);
	const [noMotorization, setNoMotorization] = useState(false);
	const widgetKey = fuelKind === 'electric' ? 'electricConsumptionPerAsset' : 'fuelConsumptionPerAsset';
	const { dateFrom, dateTo, setDateRange } = useWidgetFilters(widgetKey);
	const isLocked = !!lockedVehicleId;
	const effectiveDateFrom = isLocked ? (filters?.dateFrom ?? dateFrom) : dateFrom;
	const effectiveDateTo = isLocked ? (filters?.dateTo ?? dateTo) : dateTo;
	const selectionStorageKey = useMemo(
		() => `fleetmax_dashboard_${widgetKey}_vehicles`,
		[widgetKey],
	);
	const labels = useMemo(() => getConsumptionTranslationKeys(fuelKind, isLocked), [fuelKind, isLocked]);

	const uniqueVehicles = useMemo(() => {
		const seen = new Set<string>();
		const dedup = vehicles.filter(v => {
			if (seen.has(v.value)) return false;
			seen.add(v.value);
			return true;
		});
		// Enquanto a info dos combustíveis ainda não chegou, mostra a lista completa para evitar
		// um estado vazio momentâneo. Filtra assim que o map de tipos por ativo está disponível.
		if (!fuelKinds) return dedup;
		const allowed = new Set(
			fuelKinds
				.filter(k => fuelKind === 'electric' ? k.hasElectric : k.hasCombustion)
				.map(k => k.vehicleId),
		);
		return dedup.filter(v => allowed.has(v.value));
	}, [vehicles, fuelKinds, fuelKind]);

	useEffect(() => {
		let cancelled = false;
		DashboardService.getVehicleFuelKinds()
			.then(rows => {
				if (cancelled) return;
				setFuelKinds(rows ?? []);
			})
			.catch(error => {
				Logger.error(LOGGER_LOG_TYPE.DASHBOARD, 'error getting vehicle fuel kinds', error);
				if (!cancelled) setFuelKinds([]);
			});
		return () => { cancelled = true; };
	}, []);

	useEffect(() => {
		if (isLocked) return;
		// Só restaura a seleção guardada depois do filtro de motorização estar aplicado,
		// para não repor ativos do tipo errado gravados antes do map de combustíveis chegar.
		if (!fuelKinds) return;
		if (uniqueVehicles.length === 0 || selectedVehicles.length > 0) return;
		try {
			const raw = localStorage.getItem(selectionStorageKey);
			if (!raw) return;
			const savedIds: string[] = JSON.parse(raw);
			const restored = savedIds
				.slice(0, MAX_VEHICLES)
				.map(id => uniqueVehicles.find(v => v.value === id))
				.filter((v): v is SelectValueLabel => v != null);
			if (restored.length >= MIN_VEHICLES) {
				setSelectedVehicles(restored);
			}
		} catch {
			// ignore
		}
		// eslint-disable-next-line
	}, [uniqueVehicles, selectionStorageKey]);

	const fetchData = useCallback(async (vehicleIds: string[]) => {
		if (vehicleIds.length < MIN_VEHICLES) {
			setData(null);
			return;
		}
		try {
			setIsLoading(true);
			const result = await DashboardService.getFuelConsumptionPerAsset(vehicleIds, fuelKind, effectiveDateFrom, effectiveDateTo);
			setData(result || null);
		} catch (error) {
			addToast(t('common.messages.error_load_info'), { appearance: 'error' });
			Logger.error(LOGGER_LOG_TYPE.DASHBOARD, 'error getting fuel consumption per asset', error);
		} finally {
			setIsLoading(false);
		}
		// eslint-disable-next-line
	}, [effectiveDateFrom, effectiveDateTo, fuelKind]);

	// Modo comparação (não fixo): segue a seleção do utilizador.
	useEffect(() => {
		if (isLocked) return;
		const ids = selectedVehicles.map(v => v.value);
		fetchData(ids);
	}, [selectedVehicles, fetchData, isLocked]);

	// Modo "veículo fixo": só carrega quando o ativo tem a motorização relevante.
	useEffect(() => {
		if (!isLocked || !lockedVehicleId) return;
		if (!fuelKinds) return; // aguarda info de combustíveis por ativo
		const row = fuelKinds.find(k => k.vehicleId === lockedVehicleId);
		const hasKind = fuelKind === 'electric' ? !!row?.hasElectric : !!row?.hasCombustion;
		if (!hasKind) {
			setNoMotorization(true);
			setData(null);
			return;
		}
		setNoMotorization(false);
		fetchData([lockedVehicleId]);
	}, [isLocked, lockedVehicleId, fuelKinds, fuelKind, fetchData]);

	const handleVehicleChange = (values: any) => {
		const selected: SelectValueLabel[] = values || [];
		if (selected.length > MAX_VEHICLES) return;
		setSelectedVehicles(selected);
		try {
			localStorage.setItem(selectionStorageKey, JSON.stringify(selected.map(v => v.value)));
		} catch {
			// ignore
		}
	};

	const monthPicker = <WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} />;

	const selector = (
		<div className={styles.selectorWrapper}>
			<Select
				options={uniqueVehicles}
				value={selectedVehicles}
				onChange={handleVehicleChange}
				isMulti
				isClearable
				slim
				usePortal
				placeholder={t(labels.selectPlaceholder as any, { max: MAX_VEHICLES }) as string}
			/>
			{monthPicker}
		</div>
	);

	const renderBody = () => {
		if (isLoading) {
			return <div className={styles.centerAlign}><LoadingSpinner /></div>;
		}
		if (isLocked) {
			// Ativo sem a motorização relevante (ex.: consumo elétrico num ativo a gasóleo).
			if (noMotorization) {
				return <div className={styles.emptyMessage}>{t(labels.noMotorization as any) as string}</div>;
			}
			if (!data) {
				return <div className={styles.centerAlign}><LoadingSpinner /></div>;
			}
		} else {
			if (selectedVehicles.length < MIN_VEHICLES) {
				return <div className={styles.emptyMessage}>{t(labels.selectHint as any, { max: MAX_VEHICLES }) as string}</div>;
			}
			if (!data) {
				return <div className={styles.centerAlign}><LoadingSpinner /></div>;
			}
		}
		// Re-key força o chart a montar de fresco a cada mudança de seleção / período.
		// Sem isto, o useElementSize por vezes mede 0 num frame em transições e o SVG fica vazio.
		const chartKey = `${data.items.map(i => i.vehicleId).join('-')}|${effectiveDateFrom?.getTime() ?? 0}|${effectiveDateTo?.getTime() ?? 0}`;
		return <FuelConsumptionPerAssetChart key={chartKey} data={data} fuelKind={fuelKind} />;
	};

	return (
		<Box
			className={tableStyles.container}
			title={t(labels.title as any) as string}
			subTitleTooltip={t(labels.tooltip as any) as string}
			renderOptions={isLocked ? monthPicker : selector}
		>
			{renderBody()}
		</Box>
	);
};

export default FuelConsumptionPerAsset;
